import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../../components/Sidebar";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomButton from "../../../components/CustomButton";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import WalletController from "../../../controller/walletController";
import ExchangeController from "../../../controller/exchangeController";
import { ensureLocation } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { useToast } from "../../../contexts/ToastContext";
import { openSupportChatOnCurrentPage } from "../../../utils/supportChatNavigation";
import { useTranslation } from "react-i18next";
import "../../css/cachIn.css";
import { isAccountApprovalLocked } from "../../../utils/permissionHelper";
import ReactCountryFlag from "react-country-flag";
import { getFlagCountryCodeFromParts } from "../../../utils/currencyFlagHelper";

/**
 * Funds → Exchange page
 * Mirrors mobile ExchangeMoneyScreen behavior using existing web components.
 */
export default function Exchange() {
  const { t } = useTranslation();
  const { success: showSuccess, error: showError, warning: showWarning } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [wallets, setWallets] = useState([]);

  const [currencies, setCurrencies] = useState([]); // From currencies (filtered by user's wallets)
  const [allCurrencies, setAllCurrencies] = useState([]); // To currencies (all available)
  const [fromCurrency, setFromCurrency] = useState(null);
  const [toCurrency, setToCurrency] = useState(null);

  const [amount, setAmount] = useState("");
  const [amountAfter, setAmountAfter] = useState("");

  const [loading, setLoading] = useState(true);
  const [rateLoading, setRateLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordDialogData, setPasswordDialogData] = useState(null);

  const basedCurrency = "USD";

  // Global approval/profile lock:
  // - Profile not completed OR KYC in "Account Approval Required" → fully locked
  const approvalLocked = useMemo(() => isAccountApprovalLocked(user), [user]);

  const fromRate = useMemo(() => {
    if (!fromCurrency) return null;
    return fromCurrency?.data?.rate ?? fromCurrency.rate ?? null;
  }, [fromCurrency]);

  const toRate = useMemo(() => {
    if (!toCurrency) return null;
    return toCurrency?.data?.rate ?? toCurrency.rate ?? null;
  }, [toCurrency]);
  const filteredToCurrencies = useMemo(() => {
    if (!fromCurrency) return allCurrencies;
    const fromCode = fromCurrency?.data?.code || fromCurrency?.data?.CURRENCY_CODE || "";
    return allCurrencies.filter((opt) => {
      const toCode = opt?.data?.code || opt?.data?.CURRENCY_CODE || "";
      return toCode !== fromCode;
    });
  }, [allCurrencies, fromCurrency]);
  const exchangeRateText = useMemo(() => {
    if (!fromCurrency || !toCurrency || fromRate == null || toRate == null) return "-";
    const fromCode = fromCurrency?.data?.code || fromCurrency?.data?.CURRENCY_CODE || "-";
    const toCode = toCurrency?.data?.code || toCurrency?.data?.CURRENCY_CODE || "-";
    if (fromCode === toCode) return "-";
    if (!toRate || Number.isNaN(Number(toRate)) || Number(toRate) === 0) return "-";
    const ratio = Number(fromRate) / Number(toRate);
    if (!Number.isFinite(ratio)) return "-";
    return `1 ${fromCode} = ${ratio.toFixed(4)} ${toCode}`;
  }, [fromCurrency, toCurrency, fromRate, toRate]);

  useEffect(() => {
    if (!fromCurrency || !toCurrency) return;
    const fromCode = fromCurrency?.data?.code || fromCurrency?.data?.CURRENCY_CODE || "";
    const toCode = toCurrency?.data?.code || toCurrency?.data?.CURRENCY_CODE || "";
    if (fromCode && toCode && fromCode === toCode) {
      setToCurrency(null);
      setAmountAfter("");
    }
  }, [fromCurrency, toCurrency]);

  useEffect(() => {
    const init = async () => {
      let walletCurrencyCodes = new Set();
      let walletCurrencyTotals = {};

      try {
        setLoading(true);

        // Load cached user + wallets (Dashboard-style)
        try {
          const storedUserData = sessionStorage.getItem("userData");
          if (storedUserData) {
            setUser(JSON.parse(storedUserData));
          }
        } catch (_) {}

        try {
          const walletData = await WalletController.getWallets();
          if (walletData && Array.isArray(walletData.wallets)) {
            const walletOptions = walletData.wallets.map((w) => ({
              value: w.ID ?? w.id,
              data: w,
            }));
            setWallets(walletOptions);

            // Collect all currency codes from user wallets so the "From Currency"
            // dropdown only shows currencies that the user actually has wallets for.
            walletCurrencyCodes = new Set();
            walletCurrencyTotals = {};

            walletData.wallets.forEach((w) => {
              const d = w || {};
              const code =
                d.currencies?.CURRENCY_CODE ||
                d.CURRENCY_CODE ||
                d.currency?.CURRENCY_CODE ||
                "";
              if (!code) return;

              walletCurrencyCodes.add(code);

              const balanceRaw =
                d.CURRENT_BALENCE ||
                d.CURRENT_BALANCE ||
                d.BALANCE ||
                d.AMOUNT ||
                0;
              const num =
                typeof balanceRaw === "string"
                  ? parseFloat(balanceRaw.replace(/,/g, ""))
                  : parseFloat(balanceRaw);
              if (!Number.isNaN(num)) {
                walletCurrencyTotals[code] =
                  (walletCurrencyTotals[code] || 0) + num;
              }
            });
          }
        } catch (walletErr) {
          showError("Error Occurred", walletErr.message || "Failed to load wallets.");
        }

        // Load exchangeable currencies
        // Match Flutter: show ALL currencies from response for "To Currency"
        // But filter by user's wallets for "From Currency" (can only exchange from owned wallets)
        try {
          const { currencies: loadedCurrencies } = await ExchangeController.getExchangeCurrencies();
          
          // ALL currencies for "To Currency" dropdown (matches Flutter exactly)
          const allOptions = loadedCurrencies
            .filter((c) => c.id != null && c.id !== "")
            .map((c) => {
              const code = c.code || "-";
              // Match Flutter format: "USD (USD)"
              const label = `${code} (${code})`;
              return {
                value: c.id,
                label: label,
                data: c,
              };
            });
          
          setAllCurrencies(allOptions);
          
          // Filtered currencies for "From Currency" dropdown (only currencies user has wallets for)
          const fromOptions = loadedCurrencies
            .filter((c) => {
              if (c.id == null || c.id === "") return false;
              const code = c.code || "-";
              // Only include currencies that exist in the user's wallets
              if (walletCurrencyCodes.size > 0) {
                return walletCurrencyCodes.has(code);
              }
              return true;
            })
            .map((c) => {
              const code = c.code || "-";
              const total = walletCurrencyTotals[code];
              const hasTotal = typeof total === "number" && !Number.isNaN(total);
              // Label: "USD (USD) - 123.45 USD" when balance is known
              const balancePart = hasTotal ? ` - ${total.toFixed(2)} ${code}` : "";
              const label = `${code} (${code})${balancePart}`;
              
              return {
                value: c.id,
                label: label,
                data: c,
              };
            });
          
          setCurrencies(fromOptions);
          
          
          if (fromOptions.length === 0) {
            console.error("Exchange currencies: No From currencies available");
            showError("No Currencies", "No exchangeable currencies are available in your wallets.");
          }
        } catch (curErr) {
          console.error("Failed to load exchange currencies:", curErr);
          showError("Error Occurred", curErr.message || "Failed to load currencies.");
        }
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [showError]);

  const updateConvertedAmount = (rawAmount, updatedFromCurrency = null, updatedToCurrency = null) => {
    const from = updatedFromCurrency || (fromCurrency && fromCurrency.data) || null;
    const to = updatedToCurrency || (toCurrency && toCurrency.data) || null;

    const amt = typeof rawAmount === "number" ? rawAmount : parseFloat((rawAmount || "").replace(/,/g, ""));
    if (!from || !to || Number.isNaN(amt)) {
      setAmountAfter("");
      return;
    }

    const toCode = to.code || to.CURRENCY_CODE || "";
    const fromCode = from.code || from.CURRENCY_CODE || "";

    const toR = to.rate != null ? Number(to.rate) : null;
    const fromR = from.rate != null ? Number(from.rate) : null;

    const toFactor = toCode === basedCurrency ? 1 : toR || 1;
    const fromFactor = fromCode === basedCurrency ? 1 : fromR || 1;

    const converted = (amt * toFactor) / (fromFactor || 1);
    if (Number.isNaN(converted)) {
      setAmountAfter("");
    } else {
      setAmountAfter(converted.toFixed(2));
    }
  };

  const loadExchangeRate = async (nextFromCurrency = null, nextToCurrency = null) => {
    const fromOpt = nextFromCurrency || fromCurrency;
    const toOpt = nextToCurrency || toCurrency;

    setRateLoading(true);
    setAmountAfter("");

    if (!fromOpt || !toOpt) {
      setRateLoading(false);
      return;
    }

    const fromCode = fromOpt.data?.code || fromOpt.data?.CURRENCY_CODE || "";
    const toCode = toOpt.data?.code || toOpt.data?.CURRENCY_CODE || "";

    if (
      (fromCode === basedCurrency && toCode === basedCurrency) ||
      fromCode === toCode
    ) {
      setRateLoading(false);
      return;
    }

    try {
      const { ratesMap } = await ExchangeController.getExchangeRates(
        fromOpt.value,
        toOpt.value
      );

      const updatedFrom = { ...fromOpt.data };
      const updatedTo = { ...toOpt.data };

      if (updatedFrom.id && ratesMap[updatedFrom.id] != null) {
        updatedFrom.rate = ratesMap[updatedFrom.id];
      }
      if (updatedTo.id && ratesMap[updatedTo.id] != null) {
        updatedTo.rate = ratesMap[updatedTo.id];
      }

      setFromCurrency({
        ...fromOpt,
        data: updatedFrom,
      });
      setToCurrency({
        ...toOpt,
        data: updatedTo,
      });

      const fromRateVal = updatedFrom.rate;
      const toRateVal = updatedTo.rate;

      const fromCodeFinal = updatedFrom.code || updatedFrom.CURRENCY_CODE || "-";
      const toCodeFinal = updatedTo.code || updatedTo.CURRENCY_CODE || "-";

      if (amount) {
        updateConvertedAmount(amount, updatedFrom, updatedTo);
      }
    } catch (err) {
      showError("Error Occurred", err.message || "Failed to load exchange rates.");
    } finally {
      setRateLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    setAmount(val);
    if (errors.amount) {
      setErrors({ ...errors, amount: null });
    }
    if (val && fromCurrency && toCurrency) {
      updateConvertedAmount(val);
    } else {
      setAmountAfter("");
    }
  };

  const handleFromCurrencyChange = (opt) => {
    setFromCurrency(opt);
    if (errors.fromCurrency) {
      setErrors({ ...errors, fromCurrency: null });
    }
    if (opt && toCurrency) {
      loadExchangeRate(opt, toCurrency);
    }
  };

  const handleToCurrencyChange = (opt) => {
    setToCurrency(opt);
    if (errors.toCurrency) {
      setErrors({ ...errors, toCurrency: null });
    }
    if (fromCurrency && opt) {
      loadExchangeRate(fromCurrency, opt);
    }
  };

  const findWalletBalanceForCurrency = (currencyCode) => {
    if (!currencyCode) return 0;
    const entry = wallets.find((w) => {
      const d = w.data || {};
      const code = d.currencies?.CURRENCY_CODE || d.CURRENCY_CODE || "";
      return code === currencyCode;
    });
    if (!entry) return 0;
    const d = entry.data;
    const balanceRaw =
      d.CURRENT_BALENCE ||
      d.CURRENT_BALANCE ||
      d.BALANCE ||
      d.AMOUNT ||
      0;
    const num =
      typeof balanceRaw === "string"
        ? parseFloat(balanceRaw.replace(/,/g, ""))
        : parseFloat(balanceRaw);
    return Number.isNaN(num) ? 0 : num;
  };

  const handleProceed = async () => {
    // Block exchange when account/profile is locked
    if (approvalLocked) {
      if (showWarning) {
        showWarning(
          "Account locked",
          "You must complete your profile and wait for account approval before performing exchanges."
        );
      }
      return;
    }

    const newErrors = {};

    const amt = parseFloat((amount || "").replace(/,/g, ""));
    if (!amount || Number.isNaN(amt) || amt <= 0) {
      newErrors.amount = "Please enter a valid amount.";
    }

    if (!fromCurrency) {
      newErrors.fromCurrency = "Please select From currency.";
    }
    if (!toCurrency) {
      newErrors.toCurrency = "Please select To currency.";
    }

    if (fromCurrency && toCurrency) {
      const fromCode = fromCurrency.data?.code || fromCurrency.data?.CURRENCY_CODE || "";
      const toCode = toCurrency.data?.code || toCurrency.data?.CURRENCY_CODE || "";
      if (
        (fromCode === basedCurrency && toCode === basedCurrency) ||
        fromCode === toCode
      ) {
        newErrors.currencyPair = "Please change the currencies.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const fromCode = fromCurrency.data?.code || fromCurrency.data?.CURRENCY_CODE || "";
    const walletBalance = findWalletBalanceForCurrency(fromCode);
    if (amt > walletBalance) {
      showError("Insufficient Balance", "You don't have enough amount in the source wallet.");
      return;
    }

    try {
      const loc = await ensureLocation();
      if (!loc.ok) {
        const msg =
          loc.error === "location_denied"
            ? "Please enable location access to continue."
            : loc.error === "location_unavailable"
            ? "Location is unavailable. Please check your device settings."
            : "Location is required to continue. Please enable it.";
        showError("Action Required", msg);
        return;
      }
    } catch (_) {}

    try {
      const fromId = fromCurrency.value;
      const toId = toCurrency.value;
      const { exchangeId, amountAfterExchange } =
        await ExchangeController.calculateExchange(amt, fromId, toId);

      if (!exchangeId) {
        showError("Error Occurred", "Failed to create exchange request. Please try again.");
        return;
      }

      const fromCodeFinal = fromCurrency.data?.code || fromCurrency.data?.CURRENCY_CODE || "-";
      const toCodeFinal = toCurrency.data?.code || toCurrency.data?.CURRENCY_CODE || "-";

      const firstMessage = `You need to exchange ${amt.toFixed(2)} ${fromCodeFinal} to ${toCodeFinal}.`;
      const secondMessage = `Amount after exchange will be ${amountAfterExchange.toFixed(
        2
      )} ${toCodeFinal}.`;

      setPasswordDialogData({
        requestToken: exchangeId,
        firstMessage,
        secondMessage,
      });
      setPasswordDialogOpen(true);
    } catch (err) {
      const msg =
        err.response?.data?.MESSAGE ||
        err.response?.data?.message ||
        err.message ||
        "Failed to calculate exchange. Please try again.";
      showError("Error Occurred", msg);
    }
  };

  const refreshWalletData = async () => {
    try {
      // Force refresh to get updated balances from the server (bypass cache)
      const walletData = await WalletController.getWallets(true);
      if (walletData && Array.isArray(walletData.wallets)) {
        const walletOptions = walletData.wallets.map((w) => ({
          value: w.ID ?? w.id,
          data: w,
        }));
        setWallets(walletOptions);

        // Rebuild currency totals from refreshed wallets
        const walletCurrencyCodes = new Set();
        const walletCurrencyTotals = {};

        walletData.wallets.forEach((w) => {
          const d = w || {};
          const code =
            d.currencies?.CURRENCY_CODE ||
            d.CURRENCY_CODE ||
            d.currency?.CURRENCY_CODE ||
            "";
          if (!code) return;

          walletCurrencyCodes.add(code);

          const balanceRaw =
            d.CURRENT_BALENCE ||
            d.CURRENT_BALANCE ||
            d.BALANCE ||
            d.AMOUNT ||
            0;
          const num =
            typeof balanceRaw === "string"
              ? parseFloat(balanceRaw.replace(/,/g, ""))
              : parseFloat(balanceRaw);
          if (!Number.isNaN(num)) {
            walletCurrencyTotals[code] =
              (walletCurrencyTotals[code] || 0) + num;
          }
        });

        // Update From Currency dropdown with new balances
        const { currencies: loadedCurrencies } = await ExchangeController.getExchangeCurrencies();
        
        const fromOptions = loadedCurrencies
          .filter((c) => {
            if (c.id == null || c.id === "") return false;
            const code = c.code || "-";
            if (walletCurrencyCodes.size > 0) {
              return walletCurrencyCodes.has(code);
            }
            return true;
          })
          .map((c) => {
            const code = c.code || "-";
            const total = walletCurrencyTotals[code];
            const hasTotal = typeof total === "number" && !Number.isNaN(total);
            const balancePart = hasTotal ? ` - ${total.toFixed(2)} ${code}` : "";
            const label = `${code} (${code})${balancePart}`;
            
            return {
              value: c.id,
              label: label,
              data: c,
            };
          });
        
        setCurrencies(fromOptions);
      }
    } catch (err) {
      console.error("Failed to refresh wallet data:", err);
    }
  };

  useEffect(() => {
    const onFundsRefresh = () => {
      refreshWalletData();
    };
    window.addEventListener("mfg:funds-refresh", onFundsRefresh);
    return () => window.removeEventListener("mfg:funds-refresh", onFundsRefresh);
  }, []);

  const handlePasswordDialogClose = async (result) => {
    setPasswordDialogOpen(false);
    const status = typeof result === "string" ? result : result?.result;

    if (status === "ok") {
      showSuccess("Exchange Completed", "Your exchange request has been applied successfully.");
      // Clear form and dropdowns
      setAmount("");
      setAmountAfter("");
      setErrors({});
      setFromCurrency(null);
      setToCurrency(null);
      // Refresh wallet balances
      await refreshWalletData();
    } else if (status === "need_admin") {
      showWarning(
        "Exchange Submitted",
        "Your exchange request is pending admin approval."
      );
      // Also clear and refresh for pending requests
      setAmount("");
      setAmountAfter("");
      setErrors({});
      setFromCurrency(null);
      setToCurrency(null);
      await refreshWalletData();
    } else if (status === "canceled") {
      showWarning("Exchange Canceled", "Exchange request has been canceled.");
      setAmount("");
      setAmountAfter("");
      setErrors({});
      setFromCurrency(null);
      setToCurrency(null);
    } else if (status === "failed") {
      showError("Error Occurred", "Exchange request failed. Please try again.");
    }
  };

  return (
    <div className="cash-in-page">
      <Sidebar user={user} />
      <div className="cash-in-content">
        {loading && (
          <div className="cash-in-loading-message">{t("loading", { ns: "mobile" })}...</div>
        )}

        <div className="cash-in-container cash-in-container--styled">
          {/* When account/profile is locked, just show centered lock message (no tab titles) */}
          {approvalLocked ? (
            <div className="cash-in-tab-content cash-in-tab-content--styled">
              <div className="cash-in-locked-message">
                <i className="fa-solid fa-lock"></i>
                <p>{t("you_dont_have_access_to_do_this_operation", { ns: "mobile" })}</p>
              </div>
            </div>
          ) : (
            <div className="cash-in-tab-content cash-in-tab-content--styled">
              <div className="cash-in-layout-grid">
                <div className="cash-in-form-panel">
                  <div className="cash-in-panel-header">
                    <h2>{t("exchange_money", { ns: "mobile" })}</h2>
                    <p>{t("exchange_money_description", { ns: "mobile" })}</p>
                  </div>
                  <div className="cash-in-form">
                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">{t("amount_to_exchange", { ns: "mobile" })}</label>
                      <div className="cash-in-input-wrapper">
                        <span className="cash-in-input-icon">
                          <i className="fa-solid fa-dollar-sign"></i>
                        </span>
                        <CustomInput
                          type="text"
                          placeholder={t("enter_your_amount_to_exchange", { ns: "mobile" })}
                          value={amount}
                          onChange={handleAmountChange}
                          className={`cash-in-input ${errors.amount ? "cash-in-input-error" : ""}`}
                          disabled={loading}
                        />
                      </div>
                      {errors.amount && (
                        <div className="cash-in-error-message">{errors.amount}</div>
                      )}
                    </div>

                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">{t("from_currency", { ns: "mobile" })}</label>
                      {currencies.length === 0 && !loading && (
                        <div className="cash-in-error-message" style={{ marginTop: "8px" }}>
                          {t("no_currencies_available", { ns: "mobile" })}
                        </div>
                      )}
                      <CustomDropdown
                        options={currencies}
                        value={fromCurrency}
                        onChange={handleFromCurrencyChange}
                        placeholder={currencies.length === 0 ? t("no_currencies_available", { ns: "mobile" }) : t("select_from_currency", { ns: "mobile" })}
                        className={`cash-in-dropdown ${errors.fromCurrency ? "dropdown-error" : ""}`}
                        disabled={loading || currencies.length === 0}
                        getDisplayValue={(opt) =>
                          opt && typeof opt === "object" && opt.label != null ? String(opt.label) : ""
                        }
                        renderOption={(option, isSelected) => {
                          const d = option?.data;
                          const flag = d
                            ? getFlagCountryCodeFromParts(d.code, d.symbol, d.country)
                            : null;
                          return (
                            <div
                              className="cash-in-wallet-option"
                              style={{ display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              {flag && (
                                <ReactCountryFlag
                                  countryCode={flag}
                                  svg
                                  style={{ width: "20px", height: "15px", objectFit: "cover" }}
                                  title={d?.code || flag}
                                />
                              )}
                              <span>{option?.label ?? ""}</span>
                              {isSelected && (
                                <i className="fa-solid fa-check custom-dropdown-check"></i>
                              )}
                            </div>
                          );
                        }}
                        renderValue={(option, placeholderText) => {
                          if (!option) {
                            return (
                              <span className="custom-dropdown-placeholder">{placeholderText}</span>
                            );
                          }
                          const d = option?.data;
                          const flag = d
                            ? getFlagCountryCodeFromParts(d.code, d.symbol, d.country)
                            : null;
                          return (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              {flag && (
                                <ReactCountryFlag
                                  countryCode={flag}
                                  svg
                                  style={{ width: "20px", height: "15px", objectFit: "cover" }}
                                  title={d?.code || flag}
                                />
                              )}
                              <span>{option.label}</span>
                            </div>
                          );
                        }}
                      />
                    </div>

                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">{t("to_currency", { ns: "mobile" })}</label>
                      {allCurrencies.length === 0 && !loading && (
                        <div className="cash-in-error-message" style={{ marginTop: "8px" }}>
                          {t("no_currencies_available", { ns: "mobile" })}
                        </div>
                      )}
                      <CustomDropdown
                        options={filteredToCurrencies}
                        value={toCurrency}
                        onChange={handleToCurrencyChange}
                        placeholder={filteredToCurrencies.length === 0 ? t("no_currencies_available", { ns: "mobile" }) : t("select_to_currency", { ns: "mobile" })}
                        className={`cash-in-dropdown ${errors.toCurrency ? "dropdown-error" : ""}`}
                        disabled={loading || filteredToCurrencies.length === 0}
                        getDisplayValue={(opt) =>
                          opt && typeof opt === "object" && opt.label != null ? String(opt.label) : ""
                        }
                        renderOption={(option, isSelected) => {
                          const d = option?.data;
                          const flag = d
                            ? getFlagCountryCodeFromParts(d.code, d.symbol, d.country)
                            : null;
                          return (
                            <div
                              className="cash-in-wallet-option"
                              style={{ display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              {flag && (
                                <ReactCountryFlag
                                  countryCode={flag}
                                  svg
                                  style={{ width: "20px", height: "15px", objectFit: "cover" }}
                                  title={d?.code || flag}
                                />
                              )}
                              <span>{option?.label ?? ""}</span>
                              {isSelected && (
                                <i className="fa-solid fa-check custom-dropdown-check"></i>
                              )}
                            </div>
                          );
                        }}
                        renderValue={(option, placeholderText) => {
                          if (!option) {
                            return (
                              <span className="custom-dropdown-placeholder">{placeholderText}</span>
                            );
                          }
                          const d = option?.data;
                          const flag = d
                            ? getFlagCountryCodeFromParts(d.code, d.symbol, d.country)
                            : null;
                          return (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              {flag && (
                                <ReactCountryFlag
                                  countryCode={flag}
                                  svg
                                  style={{ width: "20px", height: "15px", objectFit: "cover" }}
                                  title={d?.code || flag}
                                />
                              )}
                              <span>{option.label}</span>
                            </div>
                          );
                        }}
                      />
                    </div>

                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">{t("amount_after_exchange", { ns: "mobile" })}</label>
                      <div className="cash-in-input-wrapper">
                        <CustomInput
                          type="text"
                          value={amountAfter}
                          readOnly
                          className="cash-in-input"
                          disabled
                        />
                      </div>
                    </div>

                    {rateLoading && (
                      <div className="cash-in-loading-message">{t("loading_exchange_rate", { ns: "mobile" })}...</div>
                    )}
                    {errors.currencyPair && (
                      <div className="cash-in-error-message">{errors.currencyPair}</div>
                    )}
                  </div>
                  <div className="cash-in-actions" style={{ marginTop: "24px" }}>
                    <CustomButton
                      className="cash-in-proceed-button"
                      variant="primary"
                      onClick={handleProceed}
                      disabled={loading || approvalLocked}
                    >
                      <span>{t("submit", { ns: "mobile" })}</span>
                    </CustomButton>
                  </div>
                </div>

                <div className="cash-in-side-panel">
                  <div className="cash-in-info-card">
                    <h3>
                      <i className="fa-solid fa-chart-line" />
                      {t("exchange_overview", { ns: "mobile", defaultValue: "Exchange Overview" })}
                    </h3>
                    <ul>
                      <li>
                        <span>{t("based_currency_label", { ns: "mobile", defaultValue: "Based Currency" })}</span>
                        <strong>{basedCurrency}</strong>
                      </li>
                      <li>
                        <span>{t("current_pair", { ns: "mobile", defaultValue: "Current Pair" })}</span>
                        <strong>
                          {(fromCurrency?.data?.code || fromCurrency?.data?.CURRENCY_CODE || "-")}{" "}
                          {t("to", { ns: "mobile", defaultValue: "to" })}{" "}
                          {(toCurrency?.data?.code || toCurrency?.data?.CURRENCY_CODE || "-")}
                        </strong>
                      </li>
                      <li>
                        <span>{t("estimated_rate", { ns: "mobile", defaultValue: "Estimated Rate" })}</span>
                        <strong>{exchangeRateText}</strong>
                      </li>
                      <li>
                        <span>{t("estimated_receive", { ns: "mobile", defaultValue: "Estimated Receive" })}</span>
                        <strong>{amountAfter || "-"}</strong>
                      </li>
                    </ul>
                  </div>

                  <div className="cash-in-help-card">
                    <h3>
                      <i className="fa-solid fa-headset" />
                      {t("contactus_title", { ns: "mobile" })}
                    </h3>
                    <p>
                      {t("cash_in_help_description", { ns: "mobile" })}
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        openSupportChatOnCurrentPage(
                          navigate,
                          location.pathname,
                          location.search
                        )
                      }
                    >
                      {t("contact_us", { ns: "mobile" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <TransactionPasswordDialog
        open={passwordDialogOpen && !!passwordDialogData}
        onClose={handlePasswordDialogClose}
        mode="exchange"
        requestToken={passwordDialogData?.requestToken ?? ""}
        firstMessage={passwordDialogData?.firstMessage ?? ""}
        secondMessage={passwordDialogData?.secondMessage ?? ""}
      />
    </div>
  );
}

